import React, { useState, useEffect } from "react";
import "./App.css";
import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Timezone options
const TIMEZONES = [
  { value: 'America/New_York', label: 'Eastern Time (ET)' },
  { value: 'America/Chicago', label: 'Central Time (CT)' },
  { value: 'America/Denver', label: 'Mountain Time (MT)' },
  { value: 'America/Los_Angeles', label: 'Pacific Time (PT)' },
  { value: 'America/Phoenix', label: 'Arizona Time (AZ)' },
  { value: 'America/Anchorage', label: 'Alaska Time (AK)' },
  { value: 'Pacific/Honolulu', label: 'Hawaii Time (HI)' }
];

const FREQUENCY_OPTIONS = [
  { value: 'daily', label: 'Daily', description: 'One uplifting quote every day' },
  { value: 'few_times_week', label: 'Few Times a Week', description: '3-4 inspiring messages per week' },
  { value: 'weekly', label: 'Weekly', description: 'One motivational quote each week' }
];

const ASTROLOGICAL_SIGNS = [
  { value: '', label: 'Skip horoscopes (quotes only)' },
  { value: 'aries', label: 'Aries (Mar 21 - Apr 19)' },
  { value: 'taurus', label: 'Taurus (Apr 20 - May 20)' },
  { value: 'gemini', label: 'Gemini (May 21 - Jun 20)' },
  { value: 'cancer', label: 'Cancer (Jun 21 - Jul 22)' },
  { value: 'leo', label: 'Leo (Jul 23 - Aug 22)' },
  { value: 'virgo', label: 'Virgo (Aug 23 - Sep 22)' },
  { value: 'libra', label: 'Libra (Sep 23 - Oct 22)' },
  { value: 'scorpio', label: 'Scorpio (Oct 23 - Nov 21)' },
  { value: 'sagittarius', label: 'Sagittarius (Nov 22 - Dec 21)' },
  { value: 'capricorn', label: 'Capricorn (Dec 22 - Jan 19)' },
  { value: 'aquarius', label: 'Aquarius (Jan 20 - Feb 18)' },
  { value: 'pisces', label: 'Pisces (Feb 19 - Mar 20)' }
];

function App() {
  const [currentStep, setCurrentStep] = useState('landing'); // 'landing', 'signup', 'verify', 'payment', 'success', 'privacy'
  const [formData, setFormData] = useState({
    phone_number: '',
    timezone: 'America/New_York',
    frequency: 'few_times_week',
    astrological_sign: ''
  });
  const [verificationCode, setVerificationCode] = useState('');
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [quotes, setQuotes] = useState([]);

  useEffect(() => {
    // Initialize quotes database and fetch sample quotes
    initializeQuotes();
    fetchSampleQuotes();
  }, []);

  const initializeQuotes = async () => {
    try {
      await axios.post(`${API}/init-quotes`);
      await axios.post(`${API}/init-horoscopes`);
      await axios.post(`${API}/init-proverbs`);
    } catch (error) {
      console.error('Error initializing quotes, horoscopes, and proverbs:', error);
    }
  };

  const fetchSampleQuotes = async () => {
    try {
      const response = await axios.get(`${API}/quotes?limit=3`);
      setQuotes(response.data);
    } catch (error) {
      console.error('Error fetching quotes:', error);
    }
  };

  const handleInputChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSignup = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      // Validate phone number format
      if (!formData.phone_number.startsWith('+1')) {
        setError('Please enter phone number in format: +1XXXXXXXXXX');
        setLoading(false);
        return;
      }

      const response = await axios.post(`${API}/users`, formData);
      setUser(response.data);
      setCurrentStep('verify');
    } catch (error) {
      setError(error.response?.data?.detail || 'Error creating account');
    } finally {
      setLoading(false);
    }
  };

  const handleVerification = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      await axios.post(`${API}/users/verify-phone`, {
        phone_number: formData.phone_number,
        verification_code: verificationCode
      });
      setCurrentStep('payment');
    } catch (error) {
      setError(error.response?.data?.detail || 'Verification failed');
    } finally {
      setLoading(false);
    }
  };

  const handleResendCode = async () => {
    setLoading(true);
    setError('');

    try {
      await axios.post(`${API}/users/resend-verification`, {
        phone_number: formData.phone_number
      });
      alert('New verification code sent!');
    } catch (error) {
      setError(error.response?.data?.detail || 'Error resending code');
    } finally {
      setLoading(false);
    }
  };

  const handlePayment = async (plan) => {
    setLoading(true);
    setError('');

    try {
      const origin_url = window.location.origin;
      const response = await axios.post(`${API}/payments/v1/checkout/session`, {
        plan: plan,
        user_id: user?.id,
        origin_url: origin_url
      });

      if (response.data.url) {
        window.location.href = response.data.url;
      }
    } catch (error) {
      setError(error.response?.data?.detail || 'Error processing payment');
      setLoading(false);
    }
  };

  const checkPaymentStatus = async (sessionId) => {
    try {
      const response = await axios.get(`${API}/payments/v1/checkout/status/${sessionId}`);
      return response.data;
    } catch (error) {
      console.error('Error checking payment status:', error);
      return null;
    }
  };

  // Check for successful payment on page load
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const sessionId = urlParams.get('session_id');
    
    if (sessionId) {
      setCurrentStep('success');
      // Poll payment status
      const pollStatus = async () => {
        const status = await checkPaymentStatus(sessionId);
        if (status && status.payment_status === 'paid') {
          // Payment successful
          setCurrentStep('success');
        }
      };
      pollStatus();
    }
  }, []);

  const LandingPage = () => (
    <div className="min-h-screen bg-gradient-to-br from-teal-50 via-emerald-50 to-green-100">
      {/* Hero Section */}
      <div className="relative py-20">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center">
            <img 
              src="https://customer-assets.emergentagent.com/job_daily-inspire-2/artifacts/yu9hjs7f_lu-logo.jpg" 
              alt="Little Uplift" 
              className="h-16 w-auto mx-auto mb-8"
            />
            <h1 className="text-4xl md:text-6xl font-bold text-gray-900 mb-6">
              Serendipitous Inspiration
            </h1>
            <p className="text-xl md:text-2xl text-gray-700 mb-8 max-w-3xl mx-auto">
              Get a surprise dose of inspiration and personalized horoscopes delivered right to your phone. 
              Random uplifting quotes and astrological insights at perfect moments when you need them most.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center items-center mb-12">
              <span className="text-lg font-semibold text-teal-600">$1.99/month</span>
              <span className="text-gray-400">or</span>
              <span className="text-lg font-semibold text-teal-600">$19.99/year</span>
            </div>
            <button 
              onClick={() => setCurrentStep('signup')}
              className="bg-teal-600 hover:bg-teal-700 text-white font-bold py-4 px-8 rounded-full text-lg transition-all duration-300 transform hover:scale-105 shadow-lg"
            >
              Start Your Daily Inspiration
            </button>
          </div>
        </div>
      </div>

      {/* Features Section */}
      <div className="py-20 bg-white">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-4xl font-bold text-center text-gray-900 mb-16">
            Why Little Uplift?
          </h2>
          <div className="grid md:grid-cols-3 gap-8">
            <div className="text-center p-6">
              <div className="w-16 h-16 bg-teal-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <span className="text-2xl">⏰</span>
              </div>
              <h3 className="text-xl font-semibold mb-4 text-teal-700">Perfect Timing</h3>
              <p className="text-gray-600">Random delivery between 8AM-10PM in your timezone. No scheduled interruptions, just pleasant surprises.</p>
            </div>
            <div className="text-center p-6">
              <div className="w-16 h-16 bg-teal-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <span className="text-2xl">✨</span>
              </div>
              <h3 className="text-xl font-semibold mb-4 text-teal-700">Quotes & Horoscopes</h3>
              <p className="text-gray-600">Real inspirational quotes from famous minds plus personalized daily horoscopes based on your astrological sign.</p>
            </div>
            <div className="text-center p-6">
              <div className="w-16 h-16 bg-teal-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <span className="text-2xl">🎯</span>
              </div>
              <h3 className="text-xl font-semibold mb-4 text-teal-700">Your Frequency</h3>
              <p className="text-gray-600">Choose daily, few times a week, or weekly delivery. Perfect mix of inspiration and horoscopes for your lifestyle.</p>
            </div>
          </div>
        </div>
      </div>

      {/* How it Works Section */}
      <div className="py-20 bg-teal-50">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl font-bold text-gray-900 mb-12">
            How Little Uplift Works
          </h2>
          <div className="grid md:grid-cols-2 gap-8 text-left">
            <div className="bg-white p-6 rounded-lg shadow-md">
              <h3 className="text-xl font-semibold mb-4 text-teal-700">UNIQUE</h3>
              <p className="text-gray-700">Little uplifts are individualized messages using an algorithm based on your phone number and astrological sign, creating personalized content just for you.</p>
            </div>
            <div className="bg-white p-6 rounded-lg shadow-md">
              <h3 className="text-xl font-semibold mb-4 text-teal-700">UNSCHEDULED</h3>
              <p className="text-gray-700">We never know when you might need a little uplift, so messages may come any time between 8am and 10pm in your timezone.</p>
            </div>
            <div className="bg-white p-6 rounded-lg shadow-md">
              <h3 className="text-xl font-semibold mb-4 text-teal-700">UNSCRIPTED</h3>
              <p className="text-gray-700">Some days your little uplift might be your horoscope; other days it might be an inspirational quote or something else entirely.</p>
            </div>
            <div className="bg-white p-6 rounded-lg shadow-md">
              <h3 className="text-xl font-semibold mb-4 text-teal-700">UNCOMPLICATED</h3>
              <p className="text-gray-700">No downloads, apps or logins necessary; just sign up once, for little uplifts every day.</p>
            </div>
          </div>
        </div>
      </div>

      {/* Sample Content Section */}
      {quotes.length > 0 && (
        <div className="py-20 bg-white">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
            <h2 className="text-3xl font-bold text-center text-gray-900 mb-12">
              Sample Messages
            </h2>
            <div className="grid gap-6">
              {quotes.map((quote, index) => (
                <div key={index} className="bg-teal-50 p-6 rounded-lg border-l-4 border-teal-500">
                  <p className="text-lg text-gray-800 italic mb-4">"{quote.text}"</p>
                  <p className="text-teal-700 font-semibold">— {quote.author}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Footer */}
      <footer className="bg-teal-800 text-white py-8">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <p className="text-teal-200 mb-4">© 2024 Little Uplift. All rights reserved.</p>
          <button
            onClick={() => setCurrentStep('privacy')}
            className="text-teal-200 hover:text-white underline"
          >
            Privacy Policy
          </button>
        </div>
      </footer>
    </div>
  );

  const SignupPage = () => (
    <div className="min-h-screen bg-gradient-to-br from-teal-50 to-emerald-100 flex items-center justify-center px-4">
      <div className="max-w-md w-full bg-white rounded-xl shadow-xl p-8">
        <div className="text-center mb-8">
          <img 
            src="https://customer-assets.emergentagent.com/job_daily-inspire-2/artifacts/yu9hjs7f_lu-logo.jpg" 
            alt="Little Uplift" 
            className="h-16 mx-auto mb-4"
          />
          <h2 className="text-3xl font-bold text-gray-900">Join Little Uplift</h2>
          <p className="text-gray-600 mt-2">Start your journey to daily inspiration</p>
        </div>

        {error && (
          <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded mb-4">
            {error}
          </div>
        )}

        <form onSubmit={handleSignup} className="space-y-6">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Phone Number
            </label>
            <input
              type="tel"
              name="phone_number"
              value={formData.phone_number}
              onChange={handleInputChange}
              placeholder="+1234567890"
              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent"
              required
            />
            <p className="text-xs text-gray-500 mt-1">Format: +1XXXXXXXXXX</p>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Your Timezone
            </label>
            <select
              name="timezone"
              value={formData.timezone}
              onChange={handleInputChange}
              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent"
              required
            >
              {TIMEZONES.map(tz => (
                <option key={tz.value} value={tz.value}>{tz.label}</option>
              ))}
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Your Astrological Sign (Optional)
            </label>
            <select
              name="astrological_sign"
              value={formData.astrological_sign}
              onChange={handleInputChange}
              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent"
            >
              {ASTROLOGICAL_SIGNS.map(sign => (
                <option key={sign.value} value={sign.value}>{sign.label}</option>
              ))}
            </select>
            <p className="text-xs text-gray-500 mt-1">Choose your sign to receive personalized horoscopes mixed with quotes</p>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-3">
              Message Frequency
            </label>
            <div className="space-y-3">
              {FREQUENCY_OPTIONS.map(option => (
                <label key={option.value} className="flex items-start space-x-3 cursor-pointer">
                  <input
                    type="radio"
                    name="frequency"
                    value={option.value}
                    checked={formData.frequency === option.value}
                    onChange={handleInputChange}
                    className="mt-1 text-teal-600"
                  />
                  <div>
                    <div className="font-medium text-gray-900">{option.label}</div>
                    <div className="text-sm text-gray-600">{option.description}</div>
                  </div>
                </label>
              ))}
            </div>
          </div>

          <button
            type="submit"
            disabled={loading}
            className="w-full bg-teal-600 hover:bg-teal-700 disabled:bg-teal-400 text-white font-bold py-3 px-4 rounded-lg transition-colors duration-300"
          >
            {loading ? 'Creating Account...' : 'Sign Up & Send Verification Code'}
          </button>
        </form>

        <div className="mt-6 text-center">
          <button
            onClick={() => setCurrentStep('landing')}
            className="text-teal-600 hover:text-teal-800 text-sm"
          >
            ← Back to Home
          </button>
        </div>
      </div>
    </div>
  );

  const VerificationPage = () => (
    <div className="min-h-screen bg-gradient-to-br from-teal-50 to-emerald-100 flex items-center justify-center px-4">
      <div className="max-w-md w-full bg-white rounded-xl shadow-xl p-8">
        <div className="text-center mb-8">
          <img 
            src="https://customer-assets.emergentagent.com/job_daily-inspire-2/artifacts/yu9hjs7f_lu-logo.jpg" 
            alt="Little Uplift" 
            className="h-16 mx-auto mb-4"
          />
          <h2 className="text-3xl font-bold text-gray-900">Verify Your Phone</h2>
          <p className="text-gray-600 mt-2">
            We sent a 6-digit code to<br/>
            <span className="font-semibold">{formData.phone_number}</span>
          </p>
        </div>

        {error && (
          <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded mb-4">
            {error}
          </div>
        )}

        <form onSubmit={handleVerification} className="space-y-6">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Verification Code
            </label>
            <input
              type="text"
              value={verificationCode}
              onChange={(e) => setVerificationCode(e.target.value)}
              placeholder="123456"
              maxLength="6"
              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent text-center text-xl font-mono"
              required
            />
          </div>

          <button
            type="submit"
            disabled={loading}
            className="w-full bg-teal-600 hover:bg-teal-700 disabled:bg-teal-400 text-white font-bold py-3 px-4 rounded-lg transition-colors duration-300"
          >
            {loading ? 'Verifying...' : 'Verify Phone Number'}
          </button>
        </form>

        <div className="mt-6 text-center space-y-2">
          <button
            onClick={handleResendCode}
            disabled={loading}
            className="text-teal-600 hover:text-teal-800 text-sm disabled:text-teal-400"
          >
            Resend verification code
          </button>
          <br/>
          <button
            onClick={() => setCurrentStep('signup')}
            className="text-gray-600 hover:text-gray-800 text-sm"
          >
            ← Back to signup
          </button>
        </div>
      </div>
    </div>
  );

  const PaymentPage = () => (
    <div className="min-h-screen bg-gradient-to-br from-teal-50 to-emerald-100 flex items-center justify-center px-4">
      <div className="max-w-lg w-full bg-white rounded-xl shadow-xl p-8">
        <div className="text-center mb-8">
          <img 
            src="https://customer-assets.emergentagent.com/job_daily-inspire-2/artifacts/yu9hjs7f_lu-logo.jpg" 
            alt="Little Uplift" 
            className="h-16 mx-auto mb-4"
          />
          <h2 className="text-3xl font-bold text-gray-900">Choose Your Plan</h2>
          <p className="text-gray-600 mt-2">Start your daily dose of inspiration</p>
        </div>

        {error && (
          <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded mb-4">
            {error}
          </div>
        )}

        <div className="space-y-4 mb-8">
          {/* Monthly Plan */}
          <div className="border-2 border-teal-200 rounded-lg p-6 hover:border-teal-400 transition-colors cursor-pointer">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-xl font-semibold">Monthly Plan</h3>
              <span className="text-2xl font-bold text-teal-600">$1.99</span>
            </div>
            <p className="text-gray-600 mb-4">Perfect for trying out Little Uplift</p>
            <button
              onClick={() => handlePayment('monthly')}
              disabled={loading}
              className="w-full bg-teal-600 hover:bg-teal-700 disabled:bg-teal-400 text-white font-bold py-3 px-4 rounded-lg transition-colors duration-300"
            >
              {loading ? 'Processing...' : 'Choose Monthly - $1.99'}
            </button>
          </div>

          {/* Yearly Plan */}
          <div className="border-2 border-green-200 rounded-lg p-6 hover:border-green-400 transition-colors cursor-pointer relative">
            <div className="absolute -top-3 left-1/2 transform -translate-x-1/2">
              <span className="bg-green-500 text-white px-3 py-1 rounded-full text-sm font-semibold">
                BEST VALUE
              </span>
            </div>
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-xl font-semibold">Yearly Plan</h3>
              <div className="text-right">
                <span className="text-2xl font-bold text-green-600">$19.99</span>
                <p className="text-sm text-gray-500">$1.67/month</p>
              </div>
            </div>
            <p className="text-gray-600 mb-4">Save $4.89 per year with annual billing</p>
            <button
              onClick={() => handlePayment('yearly')}
              disabled={loading}
              className="w-full bg-green-600 hover:bg-green-700 disabled:bg-green-400 text-white font-bold py-3 px-4 rounded-lg transition-colors duration-300"
            >
              {loading ? 'Processing...' : 'Choose Yearly - $19.99'}
            </button>
          </div>
        </div>

        <div className="text-center text-sm text-gray-500">
          <p>• Cancel anytime</p>
          <p>• Secure payment via Stripe</p>
          <p>• Start receiving quotes immediately</p>
        </div>

        <div className="mt-6 text-center">
          <button
            onClick={() => setCurrentStep('verify')}
            className="text-teal-600 hover:text-teal-800 text-sm"
          >
            ← Back to verification
          </button>
        </div>
      </div>
    </div>
  );

  const SuccessPage = () => (
    <div className="min-h-screen bg-gradient-to-br from-green-50 to-teal-100 flex items-center justify-center px-4">
      <div className="max-w-md w-full bg-white rounded-xl shadow-xl p-8 text-center">
        <img 
          src="https://customer-assets.emergentagent.com/job_daily-inspire-2/artifacts/yu9hjs7f_lu-logo.jpg" 
          alt="Little Uplift" 
          className="h-16 mx-auto mb-6"
        />
        <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
          <span className="text-4xl">🎉</span>
        </div>
        <h2 className="text-3xl font-bold text-gray-900 mb-4">Welcome to Little Uplift!</h2>
        <p className="text-gray-600 mb-6">
          Your subscription is active! You'll start receiving inspiring quotes at random times during your waking hours (8AM-10PM).
        </p>
        <div className="bg-teal-50 p-4 rounded-lg mb-6">
          <p className="text-sm text-teal-800">
            <strong>What happens next?</strong><br/>
            You'll receive your first inspirational quote within the next few hours. Each message will brighten your day at just the right moment!
          </p>
        </div>
        <button
          onClick={() => setCurrentStep('landing')}
          className="bg-teal-600 hover:bg-teal-700 text-white font-bold py-3 px-6 rounded-lg transition-colors duration-300"
        >
          Back to Home
        </button>
      </div>
    </div>
  );

  const PrivacyPolicyPage = () => (
    <div className="min-h-screen bg-white py-12">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-12">
          <img 
            src="https://customer-assets.emergentagent.com/job_daily-inspire-2/artifacts/yu9hjs7f_lu-logo.jpg" 
            alt="Little Uplift" 
            className="h-16 mx-auto mb-6"
          />
          <h1 className="text-4xl font-bold text-gray-900 mb-4">Privacy Policy</h1>
          <p className="text-gray-600">Last updated: {new Date().toLocaleDateString()}</p>
        </div>

        <div className="prose prose-lg max-w-none">
          <h2 className="text-2xl font-semibold text-teal-700 mb-4">Our Commitment to Your Privacy</h2>
          <p className="mb-6">
            At Little Uplift, we respect your privacy and are committed to protecting your personal information. 
            This Privacy Policy explains how we collect, use, and safeguard your information when you use our SMS-based 
            inspirational quote service.
          </p>

          <h2 className="text-2xl font-semibold text-teal-700 mb-4">Information We Collect</h2>
          <h3 className="text-xl font-medium text-gray-800 mb-3">Information You Provide:</h3>
          <ul className="list-disc pl-6 mb-4">
            <li><strong>Phone Number:</strong> We collect your phone number to send you inspirational quotes via SMS</li>
            <li><strong>Timezone:</strong> We collect your timezone to send messages during appropriate hours (8AM-10PM)</li>
            <li><strong>Message Frequency Preference:</strong> Your choice of daily, few times per week, or weekly delivery</li>
            <li><strong>Payment Information:</strong> Processed securely through Stripe (we don't store your payment details)</li>
          </ul>

          <h3 className="text-xl font-medium text-gray-800 mb-3">Information Automatically Collected:</h3>
          <ul className="list-disc pl-6 mb-6">
            <li>Service usage data (delivery confirmations, message statistics)</li>
            <li>Technical information necessary for service operation</li>
          </ul>

          <h2 className="text-2xl font-semibold text-teal-700 mb-4">How We Use Your Information</h2>
          <ul className="list-disc pl-6 mb-6">
            <li>To deliver inspirational quotes via SMS to your phone number</li>
            <li>To respect your timezone and delivery preferences</li>
            <li>To process payments and manage your subscription</li>
            <li>To prevent sending duplicate quotes within the same year</li>
            <li>To provide customer support when needed</li>
            <li>To improve our service quality</li>
          </ul>

          <h2 className="text-2xl font-semibold text-teal-700 mb-4">Information Sharing</h2>
          <p className="mb-4">We do NOT sell, trade, or rent your personal information to third parties. We only share your information in these limited circumstances:</p>
          <ul className="list-disc pl-6 mb-6">
            <li><strong>Service Providers:</strong> With Twilio (SMS delivery) and Stripe (payment processing) to operate our service</li>
            <li><strong>Legal Requirements:</strong> When required by law or to protect our rights and users</li>
            <li><strong>Business Transfers:</strong> In the event of a merger or acquisition (users will be notified)</li>
          </ul>

          <h2 className="text-2xl font-semibold text-teal-700 mb-4">Data Security</h2>
          <p className="mb-6">
            We implement appropriate technical and organizational measures to protect your personal information against 
            unauthorized access, alteration, disclosure, or destruction. Your payment information is processed securely 
            through Stripe and is never stored on our servers.
          </p>

          <h2 className="text-2xl font-semibold text-teal-700 mb-4">Your Rights and Choices</h2>
          <ul className="list-disc pl-6 mb-6">
            <li><strong>Unsubscribe:</strong> Reply "STOP" to any message to immediately unsubscribe</li>
            <li><strong>Data Access:</strong> Request a copy of your personal data we hold</li>
            <li><strong>Data Correction:</strong> Request correction of inaccurate personal data</li>
            <li><strong>Data Deletion:</strong> Request deletion of your personal data (account closure)</li>
            <li><strong>Subscription Management:</strong> Modify your frequency preferences or cancel subscription</li>
          </ul>

          <h2 className="text-2xl font-semibold text-teal-700 mb-4">SMS Terms</h2>
          <ul className="list-disc pl-6 mb-6">
            <li>Message frequency depends on your selected plan</li>
            <li>Message and data rates may apply</li>
            <li>Carriers are not liable for delayed or undelivered messages</li>
            <li>Reply "STOP" to opt out or "HELP" for support</li>
          </ul>

          <h2 className="text-2xl font-semibold text-teal-700 mb-4">Data Retention</h2>
          <p className="mb-6">
            We retain your information for as long as your account is active or as needed to provide services. 
            When you cancel your subscription, we may retain some information for legitimate business purposes 
            such as fraud prevention and legal compliance.
          </p>

          <h2 className="text-2xl font-semibold text-teal-700 mb-4">Children's Privacy</h2>
          <p className="mb-6">
            Our service is not intended for children under 13. We do not knowingly collect personal information 
            from children under 13. If you are a parent and believe your child has provided us with personal 
            information, please contact us.
          </p>

          <h2 className="text-2xl font-semibold text-teal-700 mb-4">Changes to This Policy</h2>
          <p className="mb-6">
            We may update this Privacy Policy from time to time. We will notify you of any changes by posting 
            the new policy on this page and updating the "Last updated" date. Continued use of our service 
            after changes constitutes acceptance of the new policy.
          </p>

          <h2 className="text-2xl font-semibold text-teal-700 mb-4">Contact Us</h2>
          <p className="mb-6">
            If you have any questions about this Privacy Policy or our practices, please contact us at:
          </p>
          <div className="bg-teal-50 p-4 rounded-lg mb-8">
            <p><strong>Email:</strong> privacy@littleuplift.com</p>
            <p><strong>Address:</strong> Little Uplift, Privacy Department</p>
            <p><strong>SMS Support:</strong> Reply "HELP" to any message</p>
          </div>
        </div>

        <div className="text-center mt-12">
          <button
            onClick={() => setCurrentStep('landing')}
            className="bg-teal-600 hover:bg-teal-700 text-white font-bold py-3 px-6 rounded-lg transition-colors duration-300"
          >
            Back to Home
          </button>
        </div>
      </div>
    </div>
  );

  // Render current step
  switch (currentStep) {
    case 'signup':
      return <SignupPage />;
    case 'verify':
      return <VerificationPage />;
    case 'payment':
      return <PaymentPage />;
    case 'success':
      return <SuccessPage />;
    case 'privacy':
      return <PrivacyPolicyPage />;
    default:
      return <LandingPage />;
  }
}

export default App;